import React, { useEffect, useState } from 'react';
import { Device, Position } from '../../types';
import { VehicleConfigOut, OdometerToday } from '../../services/api';
import { parseAttributes } from '../../utils/parseAttributes';
import { formatCanValue } from '../../utils/canFields';
import { parseUtc, useDataAge } from '../../utils/dataAge';
import { useAuthStore } from '../../store/authStore';
import './DeviceDetails.css';
import CommandPanel from './CommandPanel';

interface Props {
  device: Device | null;
  position: Position | null;
  isOpen: boolean;
  onClose: () => void;
  vehicleConfig: VehicleConfigOut | null;
  drivingTime?: string;
  driveStart?: string | null;
  driveEnd?: string | null;
  odometerToday?: OdometerToday | null;
}

const fetchAddress = async (lat: number, lng: number): Promise<string> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=pl`,
      { headers: { 'User-Agent': 'FleetApp/1.0' } }
    );
    const data = await res.json();
    const r = data.address;
    const parts = [r.road, r.house_number, r.city || r.town || r.village].filter(Boolean);
    return parts.join(' ') || data.display_name?.split(',').slice(0, 2).join(',') || '—';
  } catch {
    return '—';
  }
};

// Rows with value===null/undefined/'' are hidden entirely (never render "—").
const GRow = ({ label, value, unit = '' }: { label: string; value: string | number | null | undefined; unit?: string }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="dd-grid-row">
      <span className="dd-grid-label">{label}</span>
      <span className="dd-grid-value">{value}{unit && <span className="dd-grid-unit"> {unit}</span>}</span>
    </div>
  );
};

/** Small key glyph — SVG so ignition color (green/grey) is fully CSS-controlled. */
const KeyIcon = ({ on }: { on: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className={`dd-key-icon ${on ? 'on' : 'off'}`}>
    <circle cx="7" cy="12" r="4.5" stroke="currentColor" strokeWidth="2.2" />
    <path d="M11.3 12H21M17 12V15.5M20.3 12V14.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

/** 4-bar GSM strength icon: >-85 full, -85..-95 three, -95..-105 two, <-105 one (amber). */
const GsmBars = ({ rssi }: { rssi: number | null }) => {
  if (rssi == null) return null;
  const bars = rssi > -85 ? 4 : rssi >= -95 ? 3 : rssi >= -105 ? 2 : 1;
  const weak = rssi < -105;
  return (
    <span className="dd-gsm-bars" title={`${rssi} dBm`}>
      {[1, 2, 3, 4].map((i) => (
        <i key={i} className={`dd-gsm-bar${i <= bars ? (weak ? ' filled weak' : ' filled') : ''}`} style={{ height: `${2 + i * 2}px` }} />
      ))}
    </span>
  );
};

const FuelBar = ({ pct }: { pct: number }) => {
  const clamped = Math.min(100, Math.max(0, pct));
  const color = clamped < 10 ? '#e53935' : clamped < 25 ? '#ff8f00' : '#43a047';
  return (
    <div className="dd-fuel-bar-wrap">
      <div className="dd-fuel-bar-fill" style={{ width: `${clamped}%`, backgroundColor: color }} />
    </div>
  );
};

const DeviceDetails: React.FC<Props> = ({
  device, position, isOpen, onClose,
  vehicleConfig, drivingTime, driveStart, driveEnd, odometerToday,
}) => {
  const [address, setAddress] = useState<string>('ładowanie...');

  useEffect(() => {
    if (position) {
      setAddress('ładowanie...');
      fetchAddress(position.latitude, position.longitude).then(setAddress);
    }
  }, [position?.latitude, position?.longitude]);

  // Hooks must run unconditionally — age of the last position, live-updating.
  const age = useDataAge(position?.fixtime ?? null);
  // Sekcja komend: wyłącznie admin. To tylko UX (backend egzekwuje to samo
  // niezależnie — patrz require_admin w commands.py, ukrycie w React to nie
  // zabezpieczenie).
  const isAdmin = useAuthStore((s) => s.user?.administrator ?? false);

  if (!device || !isOpen) return null;

  const attrs       = position ? parseAttributes(position.attributes) : {};
  const deviceAttrs = parseAttributes(device.attributes);

  // --- podstawowe sygnały ---
  const ignition  = attrs.ignition;
  const power     = typeof attrs.power === 'number' ? attrs.power : null;
  const sat       = typeof attrs.sat === 'number' ? attrs.sat : null;
  const rssi      = typeof attrs.rssi === 'number' ? attrs.rssi : null;

  // --- prędkość GPS (nagłówek) ---
  const gpsSpeed = position?.speed_kmh != null ? Math.round(position.speed_kmh) : null;

  const isHorta   = (vehicleConfig?.device_type ?? '').toLowerCase().includes('horta');
  const isHybrid  = vehicleConfig?.is_hybrid ?? false;

  // --- stan stale/offline ---
  const isDeviceOnline = device.status?.trim() === 'online';
  const isStale = !isDeviceOnline || age.tier === 'stale';
  const fixtimeHM = position?.fixtime ? parseUtc(position.fixtime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : null;

  // ── Blok "Pojazd" ──────────────────────────────────────────────────────
  const odometerGpsOffset = vehicleConfig?.odometer_gps_offset ?? 0;
  // Horta: canOdometer z CP1 (już oczyszczone z sentineli przez decode_record — null = brak).
  // Teltonika: io87 (metry→km); zero traktowane jak sentinel (konwencja istniejących io-kanałów).
  const canOdoKm = isHorta
    ? (typeof attrs.canOdometer === 'number' ? attrs.canOdometer.toFixed(1) : null)
    : (typeof attrs.io87 === 'number' && attrs.io87 !== 0 ? (attrs.io87 / 1000).toFixed(1) : null);
  const obdSpeed = isHorta
    ? (typeof attrs.obdSpeed === 'number' ? attrs.obdSpeed.toFixed(0) : null)
    : (typeof attrs.io81 === 'number' && attrs.io81 > 0 ? formatCanValue('io81', attrs.io81)
       : typeof attrs.io24 === 'number' && attrs.io24 > 0 ? attrs.io24.toFixed(0) : null);
  const gpsDistKm = typeof attrs.totalDistance === 'number'
    ? (attrs.totalDistance / 1000 - odometerGpsOffset).toFixed(1) : null;

  // ── Blok "Paliwo" ──────────────────────────────────────────────────────
  // tankCapacity1: atrybut urządzenia (Horta INIT FT1) — automatyczny, ma priorytet
  // nad ręcznym ustawieniem VehicleConfig (Teltonika, gdzie tankCapacity1 nie istnieje).
  const tankCapacity1 = typeof deviceAttrs.tankCapacity1 === 'number' ? deviceAttrs.tankCapacity1 : null;
  const tankL = tankCapacity1 ?? vehicleConfig?.tank_capacity_liters ?? null;

  const fuelPercent = isHorta
    ? (typeof attrs.fuelLevel === 'number' ? attrs.fuelLevel : null)
    : (typeof attrs.io89 === 'number' ? attrs.io89 : null);
  const fuelLitersDirect = !isHorta && typeof attrs.io84 === 'number' ? attrs.io84 / 10 : null;
  const fuelLiters = fuelLitersDirect ?? (fuelPercent != null && tankL ? (fuelPercent / 100) * tankL : null);
  const fuelLevelStr = fuelPercent != null
    ? `${fuelPercent.toFixed(1)}%${fuelLiters != null ? ` · ${fuelLiters.toFixed(1)} L` : ''}`
    : null;

  const estRangeKm = isHorta && typeof attrs.estimatedRange === 'number' ? attrs.estimatedRange : null;
  // io107: licznik narastający od instalacji (nie zużycie dzienne) — patrz
  // komentarz w utils/canFields.ts (FMC150+LV-CAN200: L×10 ÷10, nie ÷1000).
  const fuelUsedL = isHorta
    ? (typeof attrs.totalFuelUsed === 'number' ? attrs.totalFuelUsed.toFixed(1) : null)
    : (typeof attrs.io107 === 'number' && attrs.io107 !== 0 ? formatCanValue('io107', attrs.io107) : null);

  const fuelTodayL = odometerToday?.fuel_today_l != null ? odometerToday.fuel_today_l.toFixed(1) : null;

  const hasPaliwo = fuelLevelStr != null || estRangeKm != null || fuelUsedL != null || fuelTodayL != null;

  // ── Blok "Silnik" ──────────────────────────────────────────────────────
  // Horta CP1: sentinele już wycięte w decode_record — null = brak, 0 to realna wartość
  // (poza RPM=0, gdzie u hybryd oznacza jazdę EV, patrz docs/horta-integration.md §8).
  const rpmRaw = isHorta
    ? (typeof attrs.rpm === 'number' ? attrs.rpm : null)
    : (typeof attrs.io85 === 'number' && attrs.io85 !== 0 ? attrs.io85 : null);
  const rpmStr = rpmRaw == null ? null
    : (rpmRaw === 0 && isHybrid) ? '0 (EV)'
    : rpmRaw === 0 && !isHorta ? null   // Teltonika: 0 = brak danych (istniejąca konwencja)
    : rpmRaw.toFixed(0);

  const coolantTemp = isHorta
    ? (typeof attrs.coolantTemp === 'number' ? attrs.coolantTemp : null)
    : (typeof attrs.io70 === 'number' && attrs.io70 !== 0 ? attrs.io70
       : typeof attrs.io58 === 'number' && attrs.io58 !== 0 ? attrs.io58 : null);
  const throttle = isHorta
    ? (typeof attrs.throttle === 'number' ? attrs.throttle.toFixed(1) : null)
    : (typeof attrs.io82 === 'number' && attrs.io82 !== 0 ? attrs.io82.toFixed(0) : null);
  const checkEngine = typeof attrs.checkEngine === 'boolean' ? attrs.checkEngine : null;

  const hasSilnik = rpmStr != null || coolantTemp != null || throttle != null || checkEngine != null;

  // ── przebieg dziś ──────────────────────────────────────────────────────
  const todayKm = odometerToday?.can_today_km != null
    ? odometerToday.can_today_km.toFixed(1)
    : odometerToday?.gps_today_km != null
      ? odometerToday.gps_today_km.toFixed(1)
      : null;

  const registration = vehicleConfig?.registration_number ?? null;

  return (
    <div className="dd-panel">
      {/* Nagłówek — jeden wiersz: nazwa, prędkość, stacyjka, napięcie, satelity, GSM, wiek danych */}
      <div className="dd-header">
        <div className="dd-title">
          <strong>{device.name}</strong>
          {registration && <span className="dd-registration"> {registration}</span>}
          {device.model && <span className="dd-model"> · {device.model}</span>}
        </div>

        <span className="dd-hdr-speed">{gpsSpeed != null ? `${gpsSpeed} km/h` : '— km/h'}</span>

        <span className="dd-hdr-item" title="Stacyjka">
          <KeyIcon on={!!ignition} />
          {ignition ? 'ON' : 'OFF'}
        </span>

        {power != null && (
          <span className="dd-hdr-item dd-hdr-neutral" title="Napięcie zasilania">
            ⚡ {power.toFixed(1)}V
          </span>
        )}

        {sat != null && (
          <span className="dd-hdr-item dd-hdr-neutral" title="Satelity">
            🛰 {sat}
          </span>
        )}

        {rssi != null && (
          <span className="dd-hdr-item dd-hdr-neutral" title="Sygnał GSM">
            <GsmBars rssi={rssi} />
          </span>
        )}

        <span className={`dd-hdr-age dd-age-${age.tier}`} title={age.fullUtc}>
          {isStale && fixtimeHM ? `${age.label} (${fixtimeHM})` : age.label}
        </span>

        <button className="dd-close" onClick={onClose}>✕</button>
      </div>

      {/* Siatka — repeat(auto-fit, minmax(170px,1fr)): kolumny bez zawartości nie renderują się, reszta się rozciąga */}
      <div className="dd-grid">
        <div className="dd-block">
          <div className="dd-block-title">Pojazd</div>
          <GRow label="Przebieg (CAN)" value={canOdoKm} unit="km" />
          <GRow label="Prędkość OBD" value={obdSpeed} unit="km/h" />
          <GRow label="Dystans GPS (od aktywacji)" value={gpsDistKm} unit="km" />
          <GRow label="Przebieg dziś" value={todayKm} unit="km" />
          <GRow label="Czas jazdy" value={drivingTime || null} />
          <GRow label="Początek jazdy" value={driveStart || null} />
          <GRow label="Koniec jazdy" value={driveEnd || null} />
          <GRow label="Adres" value={address} />
        </div>

        {hasPaliwo && (
          <div className="dd-block">
            <div className="dd-block-title">Paliwo</div>
            {fuelLevelStr != null && (
              <div className="dd-grid-row dd-fuel-row">
                <span className="dd-grid-label">Poziom</span>
                <span className="dd-grid-value">{fuelLevelStr}</span>
              </div>
            )}
            {fuelPercent != null && <FuelBar pct={fuelPercent} />}
            <GRow label="Zasięg est." value={estRangeKm} unit="km" />
            <GRow label="Zużyte łącznie (CAN)" value={fuelUsedL} unit="L" />
            <GRow label="Zużyte dziś" value={fuelTodayL} unit="L" />
          </div>
        )}

        {hasSilnik && (
          <div className="dd-block">
            <div className="dd-block-title">Silnik</div>
            <GRow label="RPM" value={rpmStr} />
            <GRow label="Temp. płynu" value={coolantTemp} unit="°C" />
            <GRow label="Przepustnica" value={throttle} unit="%" />
            {checkEngine != null && (
              <div className="dd-grid-row">
                <span className="dd-grid-label">Check Engine</span>
                <span className={`dd-grid-value ${checkEngine ? 'dd-badge-bad' : 'dd-badge-ok'}`}>
                  {checkEngine ? 'AKTYWNA' : 'OK'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {isAdmin && <CommandPanel deviceId={device.id} />}

      {position && (
        <div className="dd-footer">
          GPS: {position.latitude.toFixed(5)}, {position.longitude.toFixed(5)}
          &nbsp;·&nbsp;
          {parseUtc(position.fixtime).toLocaleString('pl-PL')}
        </div>
      )}
    </div>
  );
};

export default DeviceDetails;
